/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable array-callback-return */
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import React, { useMemo } from 'react';
import CrudTable from 'modules/clients/details/crudTable.component';
import { withStyles } from '@material-ui/core';

import { Card, CardContent } from '@mui/material';
import styles from 'modules/intake/styles';
import { LINE_ITEM_STATUS } from 'modules/enumerations/enumerationIds';
import { formatAmount, formatMoney } from 'utilities/currencyFormatter';

const isLineItemApproved = lineItem => {
  return lineItem?.lineItemStatusId === LINE_ITEM_STATUS.APPROVED;
};

const getLineItemDueDate = lineItem => lineItem.dueDate || lineItem.payrollDate || lineItem.expenseDate || lineItem.reportDate || null;

const sortByDueDateDesc = (a, b) => {
  const da = getLineItemDueDate(a);
  const db = getLineItemDueDate(b);

  if (!da && !db) return 0;
  if (!da) return 1;
  if (!db) return -1;

  return new Date(db) - new Date(da);
};

const buildExpenseLineItemTableData = lineItems => {
  if (!Array.isArray(lineItems)) return [];
  return [...lineItems].filter(li => !isLineItemApproved(li)).sort((a, b) => sortByDueDateDesc(a, b));
};

const buildVoucherLineItemTableData = lineItems => {
  if (!Array.isArray(lineItems)) return [];
  return [...lineItems].sort((a, b) => sortByDueDateDesc(a, b));
};

const getRpDueDateForSort = rp => rp.dueDate || rp.nextRunDate || rp.payrollDate || rp.invoiceDate || null;

const buildRecurringPayableTableData = (recurringPayables, transferee, vendors, rpStatuses = []) => {
  if (!Array.isArray(recurringPayables)) return [];

  const flat = recurringPayables.map(dto => {
    const rp = dto.recurringPayableInformation || {};

    const payeeInfo = dto.payee || {};
    let payeeName = '';
    let payToLabel = '';
    if (payeeInfo) {
      if (payeeInfo.authorizationId) {
        payeeName = `${transferee.firstName} ${transferee.lastName}`;
        payToLabel = 'EE';
      } else {
        const selectedVendor = vendors.find(s => s.vendorId === payeeInfo.vendorId);
        if (selectedVendor) {
          payeeName = selectedVendor.companyName;
        }
        payToLabel = 'Vendor';
      }
    }

    return {
      recurringPayableId: rp.recurringPayableId,
      recurringPayableNumber: rp.recurringPayableId?.toString() ?? '',
      statusName: rpStatuses.find(s => s.id === rp.recurringPayableStatusId)?.title ?? '',
      amount: rp.amount,
      payTo: payToLabel,
      payeeName: payeeName,
      startDate: rp.startDate,
      endDate: rp.endDate,
      dueDate: rp.dueDate
    };
  });

  return flat.sort((a, b) => sortByDueDateDesc(a, b, getRpDueDateForSort));
};

const recurringPayableColumns = [
  {
    header: 'id',
    accessorKey: 'recurringPayableId',
    type: 'number',
    hideInTable: true
  },
  {
    header: 'RP #',
    accessorKey: 'recurringPayableNumber',
    type: 'text',
    size: 50
  },
  {
    header: 'Status',
    accessorKey: 'statusName',
    type: 'text',
    size: 75
  },
  {
    header: 'Total Amount',
    accessorKey: 'amount',
    type: 'money',
    size: 60,
    Cell: ({ cell }) => (cell.getValue() ? formatMoney(cell.getValue()) : '-')
  },
  {
    header: 'Pay To',
    accessorKey: 'payTo',
    type: 'text',
    size: 60
  },
  {
    header: 'Payee',
    accessorKey: 'payeeName',
    type: 'text',
    size: 60
  },
  {
    header: 'Start Date',
    accessorKey: 'startDate',
    type: 'date',
    size: 75,
    Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
  },
  {
    header: 'End Date',
    accessorKey: 'endDate',
    type: 'date',
    size: 75,
    Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
  }
];

const SubmittedExpensesScreen = props => {
  const { voucherLineItems, expenseLineItems, recurringPayables, history, expenseCodes, vendors, enumerations, transferee } = props;
  const safeVendors = vendors && Array.isArray(vendors) ? vendors : [];

  const recurringPayableStatuses =
    enumerations && enumerations.recurringPayableStatuses && Array.isArray(enumerations.recurringPayableStatuses)
      ? enumerations.recurringPayableStatuses
      : [];

  const lineItemStatuses =
    enumerations && enumerations.lineItemStatuses && Array.isArray(enumerations.lineItemStatuses) ? enumerations.lineItemStatuses : [];

  const voucherStatuses =
    enumerations && enumerations.voucherStatuses && Array.isArray(enumerations.voucherStatuses) ? enumerations.voucherStatuses : [];

  const getExpenseCode = expenseCodeId => {
    const defaultCode = { expenseCodeDescription: '', category: '' };
    if (!expenseCodeId || !Array.isArray(expenseCodes) || expenseCodes.length === 0) {
      return defaultCode;
    }
    return expenseCodes.find(ec => ec.expenseCodeId === expenseCodeId) || defaultCode;
  };

  const transformedVoucherLineItems = useMemo(() => {
    return (voucherLineItems || []).map(li => {
      const { expenseCodeDescription, category } = getExpenseCode(li.expenseCodeId);
      const voucherStatusName = voucherStatuses.find(s => s.id === li.voucherStatusId)?.title || 'Draft';

      return {
        ...li,
        expenseCodeDescription: expenseCodeDescription || '',
        categoryName: category || '',
        voucherStatusName: voucherStatusName
      };
    });
  }, [voucherLineItems, expenseCodes]);

  const transformedExpenseLineItems = useMemo(() => {
    return (expenseLineItems || []).map(li => {
      const { expenseCodeDescription, category } = getExpenseCode(li.expenseCodeId);
      const expenseStatusName = lineItemStatuses.find(s => s.id === li.lineItemStatusId)?.title || 'Draft';
      return {
        ...li,
        expenseCodeDescription: expenseCodeDescription || '',
        categoryName: category || li.categoryName || '',
        expenseStatusName: expenseStatusName
      };
    });
  }, [expenseLineItems, expenseCodes]);

  const voucherRows = useMemo(() => buildVoucherLineItemTableData(transformedVoucherLineItems), [transformedVoucherLineItems]);
  const expenseRows = useMemo(() => buildExpenseLineItemTableData(transformedExpenseLineItems), [transformedExpenseLineItems]);

  const voucherLineItemsColumns = [
    {
      header: 'Status',
      accessorKey: 'voucherStatusName',
      type: 'text',
      size: 75
    },
    {
      header: 'Expense Code',
      accessorKey: 'expenseCodeDescription',
      type: 'text',
      size: 75
    },
    {
      header: 'Amount',
      accessorKey: 'totalAmount',
      type: 'money',
      size: 60,
      // No '$': the currency is its own column and may not be USD.
      Cell: ({ cell }) => (cell.getValue() ? formatAmount(cell.getValue()) : '-')
    },
    {
      header: 'Description',
      accessorKey: 'description',
      type: 'text',
      size: 75
    },
    {
      header: 'Currency',
      accessorKey: 'currencyName',
      type: 'text',
      size: 75
    },
    {
      header: 'Service',
      accessorKey: 'serviceName',
      type: 'text',
      size: 75
    },
    {
      header: 'Category',
      accessorKey: 'categoryName',
      type: 'text',
      size: 60
    },
    {
      header: 'Voucher Id',
      accessorKey: 'voucherId',
      type: 'text',
      hideInTable: true,
      hideInModel: true
    }
  ];

  const expenseLineItemsColumns = [
    {
      header: 'Status',
      accessorKey: 'expenseStatusName',
      type: 'text',
      size: 75
    },
    {
      header: 'Expense Code',
      accessorKey: 'expenseCodeDescription',
      type: 'text',
      size: 75
    },
    {
      header: 'Amount',
      accessorKey: 'totalAmount',
      type: 'money',
      size: 60,
      // No '$': the currency is its own column and may not be USD.
      Cell: ({ cell }) => (cell.getValue() ? formatAmount(cell.getValue()) : '-')
    },
    {
      header: 'Description',
      accessorKey: 'description',
      type: 'text',
      size: 75
    },
    {
      header: 'Currency',
      accessorKey: 'currencyName',
      type: 'text',
      size: 75
    },
    {
      header: 'Service',
      accessorKey: 'serviceName',
      type: 'text',
      size: 75
    },
    {
      header: 'Category',
      accessorKey: 'categoryName',
      type: 'text',
      size: 60
    },
    {
      header: 'Line Item Id',
      accessorKey: 'lineItemId',
      type: 'text',
      hideInTable: true,
      hideInModel: true
    },
    {
      header: 'Expense Id',
      accessorKey: 'expenseId',
      type: 'text',
      hideInTable: true,
      hideInModel: true
    }
  ];

  return (
    <>
      <Card sx={{ marginTop: '16px', maxWidth: '1600px', margin: '16px auto' }}>
        <CardContent sx={{ padding: '10px', marginLeft: '30px' }}>
          <CrudTable
            editingMode="modal"
            columns={voucherLineItemsColumns}
            rows={voucherRows}
            hideEdit={true}
            showBasicTable={true}
            enableRowHoverHighlight={true}
            hideAdd={true}
            hideDelete={true}
            onRowClick={row => {
              const voucherId = row.original.voucherId;
              if (voucherId) {
                history.push(`/vouchers/${voucherId}`);
              }
            }}
            headerRowFields={[{ fieldHeader: '', fieldType: 'text', fieldValue: 'Vouchers Line Items' }]}
          />
        </CardContent>
      </Card>
      <Card sx={{ marginTop: '16px', maxWidth: '1600px', margin: '16px auto' }}>
        <CardContent sx={{ padding: '10px', marginLeft: '30px' }}>
          <CrudTable
            editingMode="modal"
            columns={expenseLineItemsColumns}
            rows={expenseRows}
            hideEdit={true}
            showBasicTable={true}
            enableRowHoverHighlight={true}
            hideAdd={true}
            hideDelete={true}
            headerRowFields={[{ fieldHeader: '', fieldType: 'text', fieldValue: 'Expense Line Items' }]}
            onRowClick={row => {
              const expenseId = row.original.expenseId;
              if (expenseId) {
                history.push(`/expenses/${expenseId}`);
              }
            }}
          />
        </CardContent>
      </Card>
      <Card sx={{ marginTop: '16px', maxWidth: '1600px', margin: '16px auto' }}>
        <CardContent sx={{ padding: '10px', marginLeft: '30px' }}>
          <CrudTable
            editingMode="modal"
            columns={recurringPayableColumns}
            rows={buildRecurringPayableTableData(recurringPayables, transferee, safeVendors, recurringPayableStatuses)}
            hideEdit
            hideAdd
            hideDelete
            showBasicTable={true}
            enableRowHoverHighlight={true}
            onRowClick={row => {
              const rpId = row.original.recurringPayableId;
              history.push(`/recurringPayables/${rpId}`);
            }}
            headerRowFields={[{ fieldHeader: '', fieldType: 'text', fieldValue: 'Recurring Payables' }]}
          />
        </CardContent>
      </Card>
    </>
  );
};

const mapStateToProps = state => {
  const { addressTypes, countries, provinces } = state.enumerations.allEnums;

  return {
    addressTypesEnum: addressTypes,
    countriesEnum: countries,
    provincesEnum: provinces
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    showToast
  })
)(SubmittedExpensesScreen);
